import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { getProfession, PROFESSIONS } from "../lib/professions";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Buildings, CaretDown, Check, Plus, Trash, PencilSimple, Lock } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { ProfessionPicker } from "./ProfessionPicker";

export function useUnits() {
  const [data, setData] = useState(null);
  const load = () => api.get("/units").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);
  return [data, load];
}

export function UnitSwitcher() {
  const { refreshUser } = useAuth();
  const [data, reload] = useUnits();
  if (!data || !data.enabled || data.units.length < 2) return null;
  const active = data.units.find((u) => u.id === data.active_id) || data.units[0];
  const switchTo = async (id) => {
    if (id === data.active_id) return;
    try {
      await api.post("/units-switch", { unit_id: id });
      await refreshUser();
      await reload();
      toast.success("Unidade alterada");
      window.location.assign("/app");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="unit-switcher">
          <Buildings size={16} weight="duotone" /> <span className="max-w-[160px] truncate">{active.name}</span> <CaretDown size={12} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 bg-white">
        {data.units.map((u) => {
          const p = getProfession(u.profession);
          return (
            <DropdownMenuItem key={u.id} onClick={() => switchTo(u.id)} className="flex items-center gap-2 cursor-pointer" data-testid={`unit-option-${u.id}`}>
              <span className="w-6 h-6 rounded flex items-center justify-center text-white shrink-0" style={{ backgroundColor: p.color }}><p.icon size={14} weight="fill" /></span>
              <span className="flex-1 truncate">{u.name}<span className="block text-[11px] text-slate-500">{p.label}{u.is_default ? " · principal" : ""}</span></span>
              {u.id === data.active_id && <Check size={14} className="text-emerald-600" />}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild><Link to="/app/settings#units" className="cursor-pointer text-sm">Gerenciar unidades</Link></DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UnitForm({ initial, onSave, onCancel }) {
  const [name, setName] = useState(initial?.name || "");
  const [profession, setProfession] = useState(initial?.profession || "dentista");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave({ name, profession }); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-[1fr_200px_auto] gap-2 items-end" data-testid="unit-form">
      <div><Label>Nome da unidade</Label><Input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1" placeholder="Ex: Unidade Centro" data-testid="unit-name" /></div>
      <div>
        <Label>Área</Label>
        <Select value={profession} onValueChange={setProfession}>
          <SelectTrigger className="mt-1" data-testid="unit-profession"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{PROFESSIONS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="unit-save">{saving ? "Salvando..." : "Salvar"}</Button>
        {onCancel && <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>}
      </div>
    </form>
  );
}

export function UnitsCard() {
  const { user, refreshUser } = useAuth();
  const [data, reload] = useUnits();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  if (!data) return null;
  const enabled = data.enabled;

  const create = async (payload) => {
    try { await api.post("/units", payload); toast.success("Unidade criada"); setAdding(false); await reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const update = async (payload) => {
    try { await api.put(`/units/${editing.id}`, payload); toast.success("Unidade atualizada"); setEditing(null); await reload(); await refreshUser(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Excluir a unidade "${u.name}" e TODOS os seus dados (custos, procedimentos, orçamentos)? Esta ação não pode ser desfeita.`)) return;
    try { await api.delete(`/units/${u.id}`); toast.success("Unidade excluída"); await reload(); await refreshUser(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="card-crisp p-6" id="units" data-testid="units-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading font-semibold text-lg flex items-center gap-2"><Buildings size={20} weight="duotone" className="text-indigo-600" /> Multi-unidades</h3>
          <p className="text-xs text-slate-500 mt-1">Cada unidade tem custos, materiais, profissionais, procedimentos, orçamentos e configurações próprios. Troque de unidade pelo seletor no topo.</p>
        </div>
        {enabled && !user?.team_member && !adding && <Button size="sm" onClick={() => setAdding(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="unit-add"><Plus size={14} className="mr-1" /> Nova unidade</Button>}
      </div>
      {!enabled && (
        <div className="mt-4 flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="units-locked">
          <Lock size={16} /> Disponível no plano <strong>Clínica</strong>. <Link to="/app/billing" className="underline font-semibold">Ver planos</Link>
        </div>
      )}
      {enabled && adding && <div className="mt-4"><UnitForm onSave={create} onCancel={() => setAdding(false)} /></div>}
      {enabled && (
        <ul className="mt-4 divide-y divide-slate-100">
          {data.units.map((u) => {
            const p = getProfession(u.profession);
            return (
              <li key={u.id} className="py-3" data-testid={`unit-row-${u.id}`}>
                {editing?.id === u.id ? <UnitForm initial={u} onSave={update} onCancel={() => setEditing(null)} /> : (
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-md flex items-center justify-center text-white shrink-0" style={{ backgroundColor: p.color }}><p.icon size={16} weight="fill" /></span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{u.name} {u.id === data.active_id && <span className="ml-1 text-[10px] uppercase font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">ativa</span>}</div>
                      <div className="text-xs text-slate-500">{p.label}{u.is_default ? " · unidade principal (edite em Dados acima)" : ""}</div>
                    </div>
                    {!u.is_default && !user?.team_member && (
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => setEditing(u)} data-testid={`unit-edit-${u.id}`}><PencilSimple size={16} /></Button>
                        <Button size="icon" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => remove(u)} data-testid={`unit-delete-${u.id}`}><Trash size={16} /></Button>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function ProfessionOnboarding() {
  const { user, setUser } = useAuth();
  const [value, setValue] = useState("dentista");
  const [saving, setSaving] = useState(false);
  const open = !!user && !user.team_member && !user.profession;
  const save = async () => {
    setSaving(true);
    try { await api.put("/auth/profession", { profession: value }); setUser({ ...user, profession: value }); toast.success("Tudo pronto!"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open}>
      <DialogContent className="bg-white max-w-2xl [&>button]:hidden" data-testid="profession-onboarding">
        <DialogHeader>
          <DialogTitle>Qual é a sua área de atuação?</DialogTitle>
          <DialogDescription>O sistema adapta ícones, cores, termos e a biblioteca de serviços à sua área. Você pode mudar depois em Configurações.</DialogDescription>
        </DialogHeader>
        <ProfessionPicker compact value={value} onChange={setValue} />
        <DialogFooter><Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="profession-onboarding-save">{saving ? "Salvando..." : "Começar"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

